import React, { useState, useEffect } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Users, TrendingUp, DollarSign, Building, BarChart as BarChartIcon, Trash2, AlertTriangle, Video, Settings } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';

const AdminPanel = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [revenueChart, setRevenueChart] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [showPriceDialog, setShowPriceDialog] = useState(false);
  const [newPrice, setNewPrice] = useState('49.90');
  const [currentPrice, setCurrentPrice] = useState(49.90);
  
  // Estados para exclusão de usuário com 3 confirmações
  const [deleteDialogStep, setDeleteDialogStep] = useState(0); // 0 = fechado, 1, 2, 3 = etapas
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Estados para configurações da Landing Page
  const [landingConfig, setLandingConfig] = useState({
    video_enabled: false,
    video_url: '',
    video_title: 'Veja como funciona',
    video_description: 'Conheça todas as funcionalidades do Lucro Líquido'
  });
  const [savingLanding, setSavingLanding] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchUsers();
    fetchSubscriptions();
    fetchRevenueChart();
    fetchLandingConfig();
  }, []); // eslint-disable-line

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get(`/admin/stats?user_id=${(user?.id || user?.user_id)}`);
      setStats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get(`/admin/users?user_id=${(user?.id || user?.user_id)}`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const status = filterStatus === 'all' ? '' : filterStatus;
      const response = await axiosInstance.get(
        `/admin/subscriptions?user_id=${(user?.id || user?.user_id)}${status ? `&status=${status}` : ''}`
      );
      setSubscriptions(response.data);
    } catch (error) {
      toast.error('Erro ao carregar assinaturas');
    }
  };

  const fetchRevenueChart = async () => {
    try {
      const response = await axiosInstance.get(`/admin/revenue-chart?admin_user_id=${(user?.id || user?.user_id)}`);
      setRevenueChart(response.data);
    } catch (error) {
      console.error('Erro ao carregar gráfico:', error);
    }
  };

  const fetchLandingConfig = async () => {
    try {
      const response = await axiosInstance.get(`/admin/landing-config?user_id=${(user?.id || user?.user_id)}`);
      setLandingConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar config landing:', error);
    }
  };

  const saveLandingConfig = async () => {
    setSavingLanding(true);
    try {
      await axiosInstance.put(`/admin/landing-config?user_id=${(user?.id || user?.user_id)}`, landingConfig);
      toast.success('Configurações da Landing Page salvas!');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSavingLanding(false);
    }
  };

  const toggleUserStatus = async (targetUserId) => {
    setLoading(true);

    try {
      const response = await axiosInstance.post(
        `/admin/user/${targetUserId}/toggle-status?admin_user_id=${(user?.id || user?.user_id)}`
      );
      toast.success(response.data.message);
      fetchUsers();
      fetchSubscriptions();
      fetchStats();
    } catch (error) {
      toast.error('Erro ao alterar status');
    } finally {
      setLoading(false);
    }
  };

  // Funções para excluir usuário com 3 confirmações
  const startDeleteUser = (userToRemove) => {
    setUserToDelete(userToRemove);
    setDeleteDialogStep(1);
    setDeleteConfirmText('');
  };

  const cancelDelete = () => {
    setDeleteDialogStep(0);
    setUserToDelete(null);
    setDeleteConfirmText('');
  };

  const proceedToStep2 = () => {
    setDeleteDialogStep(2);
  };

  const proceedToStep3 = () => {
    setDeleteDialogStep(3);
    setDeleteConfirmText('');
  };

  const executeDeleteUser = async () => {
    if (deleteConfirmText !== 'EXCLUIR') {
      toast.error('Digite EXCLUIR para confirmar');
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.delete(
        `/admin/user/${userToDelete.id}?admin_user_id=${(user?.id || user?.user_id)}`
      );
      toast.success(`Usuário ${userToDelete.name} excluído com sucesso!`);
      cancelDelete();
      fetchUsers();
      fetchSubscriptions();
      fetchStats();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user.role === 'admin') {
      fetchSubscriptions();
      fetchCurrentPrice();
    }
  }, [filterStatus]);
  
  const fetchCurrentPrice = async () => {
    try {
      const response = await axiosInstance.get('/system-config');
      const price = response.data.subscription_price || 49.90;
      setCurrentPrice(price);
      setNewPrice(price.toString());
    } catch (error) {
      console.error('Erro ao buscar preço:', error);
    }
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      trial: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50',
      active: 'bg-green-500/20 text-green-300 border-green-500/50',
      expired: 'bg-red-500/20 text-red-300 border-red-500/50',
      cancelled: 'bg-gray-500/20 text-gray-300 border-gray-500/50',
    };
    return colors[status] || 'bg-gray-500/20 text-gray-300 border-gray-500/50';
  };

  const getStatusLabel = (status) => {
    const labels = {
      trial: 'Trial',
      active: 'Ativo',
      expired: 'Expirado',
      cancelled: 'Cancelado',
    };
    return labels[status] || status;
  };

  const handleUpdatePrice = async () => {
    try {
      setLoading(true);
      const price = parseFloat(newPrice);
      
      if (price <= 0 || isNaN(price)) {
        toast.error('Preço deve ser maior que zero');
        return;
      }
      
      await axiosInstance.put('/system-config/price', { preco_praticado: price });
      
      toast.success(`Preço atualizado para R$ ${price.toFixed(2)}`);
      setCurrentPrice(price);
      setShowPriceDialog(false);
    } catch (error) {
      console.error('Erro ao atualizar preço:', error);
      toast.error('Erro ao atualizar preço');
    } finally {
      setLoading(false);
    }
  };

  if (user.role !== 'ADMIN' && user.role !== 'admin') {
    return null;
  }

  return (
    <div className="flex min-h-screen" data-testid="admin-panel-page">
      <Sidebar user={user} onLogout={onLogout} />

      <div className="flex-1 p-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2" data-testid="admin-panel-title">
              Painel Administrativo
            </h1>
            <p className="text-gray-400">Visão completa do sistema</p>
          </div>
          <Button
            onClick={() => setShowPriceDialog(true)}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
          >
            <DollarSign className="mr-2" size={18} />
            Alterar Preço
          </Button>
        </div>

        {/* KPIs */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="glass border-white/10 hover-lift" data-testid="admin-kpi-users">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-400 flex items-center">
                  <Users className="mr-2" size={18} />
                  Total de Usuários
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-white">{stats.total_users}</p>
              </CardContent>
            </Card>

            <Card className="glass border-white/10 hover-lift" data-testid="admin-kpi-active-subs">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-400 flex items-center">
                  <TrendingUp className="mr-2" size={18} />
                  Assinaturas Ativas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-white">{stats.active_subscriptions}</p>
              </CardContent>
            </Card>

            <Card className="glass border-white/10 hover-lift" data-testid="admin-kpi-mrr">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-400 flex items-center">
                  <DollarSign className="mr-2" size={18} />
                  MRR (Mensal)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-white">
                  R$ {stats.mrr.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </CardContent>
            </Card>

            <Card className="glass border-white/10 hover-lift" data-testid="admin-kpi-arr">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-400 flex items-center">
                  <DollarSign className="mr-2" size={18} />
                  ARR (Anual)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-white">
                  R$ {stats.arr.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Gráfico de Receita */}
        <Card className="glass border-white/10 mb-8" data-testid="admin-revenue-chart">
          <CardHeader>
            <CardTitle className="text-white flex items-center">
              <BarChartIcon className="mr-2" />
              Evolução do MRR
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenueChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="month" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
                  labelStyle={{ color: '#F3F4F6' }}
                />
                <Bar dataKey="revenue" fill="#7C3AED" name="Receita (R$)" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="users" className="w-full" data-testid="admin-tabs">
          <TabsList className="glass border-white/10 mb-6 flex-wrap">
            <TabsTrigger value="users" data-testid="admin-tab-users">Usuários</TabsTrigger>
            <TabsTrigger value="subscriptions" data-testid="admin-tab-subscriptions">Assinaturas</TabsTrigger>
            <TabsTrigger value="landing" data-testid="admin-tab-landing">Landing Page</TabsTrigger>
            <TabsTrigger value="videos" data-testid="admin-tab-videos">Vídeos Passo a Passo</TabsTrigger>
            <TabsTrigger value="educa" data-testid="admin-tab-educa">Educa Empresário</TabsTrigger>
          </TabsList>

          {/* Tab Usuários */}
          <TabsContent value="users">
            <Card className="glass border-white/10" data-testid="admin-users-table">
              <CardHeader>
                <CardTitle className="text-white">Gestão de Usuários ({users.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-white/5">
                      <TableHead className="text-gray-300">Nome</TableHead>
                      <TableHead className="text-gray-300">Email</TableHead>
                      <TableHead className="text-gray-300">Cadastro</TableHead>
                      <TableHead className="text-gray-300">Status</TableHead>
                      <TableHead className="text-gray-300">Empresas</TableHead>
                      <TableHead className="text-gray-300 text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} className="border-white/10 hover:bg-white/5" data-testid={`user-row-${u.id}`}>
                        <TableCell className="text-white font-medium">{u.name}</TableCell>
                        <TableCell className="text-gray-400">{u.email}</TableCell>
                        <TableCell className="text-gray-400">
                          {new Date(u.created_at).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusBadgeColor(u.subscription_status)}>
                            {getStatusLabel(u.subscription_status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-300">{u.companies_count}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => toggleUserStatus(u.id)}
                              disabled={loading}
                              data-testid={`toggle-user-${u.id}`}
                              className={
                                u.subscription_status === 'active'
                                  ? 'border-red-500/50 text-red-400 hover:bg-red-500/10'
                                  : 'border-green-500/50 text-green-400 hover:bg-green-500/10'
                              }
                            >
                              {u.subscription_status === 'active' ? 'Desativar' : 'Ativar'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => startDeleteUser(u)}
                              disabled={loading || u.role === 'admin'}
                              data-testid={`delete-user-${u.id}`}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                              title={u.role === 'admin' ? 'Não é possível excluir admin' : 'Excluir usuário'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Assinaturas */}
          <TabsContent value="subscriptions">
            {/* Preço da Assinatura */}
            <Card className="glass border-white/10 mb-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <DollarSign size={20} className="text-green-400" />
                  Preço da Assinatura Mensal
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Valor atual cobrado</p>
                    <p className="text-3xl font-bold text-green-400">R$ {currentPrice.toFixed(2)}<span className="text-base font-normal text-gray-400">/mês</span></p>
                  </div>
                  <Button
                    onClick={() => setShowPriceDialog(true)}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                  >
                    <Settings size={16} className="mr-2" />
                    Alterar Preço
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="glass border-white/10" data-testid="admin-subscriptions-table">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="text-white">Gestão de Assinaturas ({subscriptions.length})</CardTitle>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white" data-testid="filter-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="active">Ativas</SelectItem>
                      <SelectItem value="expired">Expiradas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-white/5">
                      <TableHead className="text-gray-300">Usuário</TableHead>
                      <TableHead className="text-gray-300">Email</TableHead>
                      <TableHead className="text-gray-300">Status</TableHead>
                      <TableHead className="text-gray-300">Início Trial</TableHead>
                      <TableHead className="text-gray-300">Fim Trial</TableHead>
                      <TableHead className="text-gray-300">Início Assinatura</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscriptions.map((sub) => (
                      <TableRow key={sub.id} className="border-white/10 hover:bg-white/5" data-testid={`subscription-row-${sub.id}`}>
                        <TableCell className="text-white font-medium">{sub.user_name}</TableCell>
                        <TableCell className="text-gray-400">{sub.user_email}</TableCell>
                        <TableCell>
                          <Badge className={getStatusBadgeColor(sub.status)}>
                            {getStatusLabel(sub.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-400">
                          {new Date(sub.trial_start).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell className="text-gray-400">
                          {new Date(sub.trial_end).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell className="text-gray-400">
                          {sub.subscription_start
                            ? new Date(sub.subscription_start).toLocaleDateString('pt-BR')
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Landing Page */}
          <TabsContent value="landing">
            <Card className="glass border-white/10" data-testid="admin-landing-config">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  Configurações da Landing Page
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Seção do Vídeo */}
                <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Video className="w-4 h-4 text-purple-400" />
                    Vídeo de Apresentação
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Toggle Ativar/Desativar */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-gray-300">Exibir vídeo na Landing Page</Label>
                        <p className="text-xs text-gray-500">Quando ativado, o vídeo aparecerá na seção principal</p>
                      </div>
                      <Button
                        variant={landingConfig.video_enabled ? "default" : "outline"}
                        onClick={() => setLandingConfig({...landingConfig, video_enabled: !landingConfig.video_enabled})}
                        className={landingConfig.video_enabled 
                          ? "bg-green-600 hover:bg-green-700" 
                          : "border-white/20 text-gray-400 hover:bg-white/10"
                        }
                      >
                        {landingConfig.video_enabled ? '✓ Ativado' : 'Desativado'}
                      </Button>
                    </div>

                    {/* URL do Vídeo */}
                    <div className="space-y-2">
                      <Label className="text-gray-300">URL do Vídeo (YouTube ou Vimeo)</Label>
                      <Input
                        type="url"
                        value={landingConfig.video_url}
                        onChange={(e) => setLandingConfig({...landingConfig, video_url: e.target.value})}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="https://www.youtube.com/watch?v=..."
                      />
                      <p className="text-xs text-gray-500">
                        Cole o link do YouTube ou Vimeo. Ex: https://www.youtube.com/watch?v=XXXXX
                      </p>
                    </div>

                    {/* Título do Vídeo */}
                    <div className="space-y-2">
                      <Label className="text-gray-300">Título da Seção</Label>
                      <Input
                        type="text"
                        value={landingConfig.video_title}
                        onChange={(e) => setLandingConfig({...landingConfig, video_title: e.target.value})}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="Veja como funciona"
                      />
                    </div>

                    {/* Descrição */}
                    <div className="space-y-2">
                      <Label className="text-gray-300">Descrição (opcional)</Label>
                      <Input
                        type="text"
                        value={landingConfig.video_description}
                        onChange={(e) => setLandingConfig({...landingConfig, video_description: e.target.value})}
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="Conheça todas as funcionalidades do sistema"
                      />
                    </div>

                    {/* Preview */}
                    {landingConfig.video_url && (
                      <div className="mt-4 p-4 bg-black/30 rounded-lg">
                        <Label className="text-gray-400 text-xs mb-2 block">Pré-visualização:</Label>
                        <div className="aspect-video rounded-lg overflow-hidden bg-black/50">
                          {landingConfig.video_url.includes('youtube.com') || landingConfig.video_url.includes('youtu.be') ? (
                            <iframe
                              className="w-full h-full"
                              src={`https://www.youtube.com/embed/${
                                landingConfig.video_url.includes('youtu.be') 
                                  ? landingConfig.video_url.split('youtu.be/')[1]?.split('?')[0]
                                  : landingConfig.video_url.split('v=')[1]?.split('&')[0]
                              }`}
                              title="Preview"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          ) : landingConfig.video_url.includes('vimeo.com') ? (
                            <iframe
                              className="w-full h-full"
                              src={`https://player.vimeo.com/video/${landingConfig.video_url.split('vimeo.com/')[1]?.split('?')[0]}`}
                              title="Preview"
                              frameBorder="0"
                              allow="autoplay; fullscreen; picture-in-picture"
                              allowFullScreen
                            />
                          ) : (
                            <div className="flex items-center justify-center h-full text-gray-500">
                              URL não reconhecida. Use YouTube ou Vimeo.
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Botão Salvar */}
                <div className="flex justify-end">
                  <Button
                    onClick={saveLandingConfig}
                    disabled={savingLanding}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  >
                    {savingLanding ? 'Salvando...' : 'Salvar Configurações'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Vídeos Passo a Passo */}
          <TabsContent value="videos">
            <Card className="glass border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  Gerenciar Vídeos - Passo a Passo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 mb-4">
                  Gerencie os vídeos tutoriais que aparecem no popup "Passo a Passo" do sistema.
                </p>
                <Button
                  onClick={() => navigate('/admin/videos')}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  data-testid="goto-admin-videos"
                >
                  <Video className="w-4 h-4 mr-2" />
                  Acessar Gerenciador de Vídeos
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab Educa Empresário */}
          <TabsContent value="educa">
            <Card className="glass border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  Gerenciar Conteúdo - Educa Empresário
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 mb-4">
                  Gerencie os vídeos e materiais educacionais da seção "Educa Empresário".
                </p>
                <Button
                  onClick={() => navigate('/admin/educa-empresario')}
                  className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
                  data-testid="goto-admin-educa"
                >
                  <Video className="w-4 h-4 mr-2" />
                  Acessar Gerenciador de Conteúdo
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog para Alterar Preço */}
      <Dialog open={showPriceDialog} onOpenChange={setShowPriceDialog}>
        <DialogContent className="glass border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Alterar Preço da Assinatura</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Preço Atual: R$ {currentPrice.toFixed(2)}</Label>
              <Label className="text-gray-300">Novo Preço Mensal (R$)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                placeholder="49.90"
              />
              <p className="text-xs text-gray-500">
                Este será o novo valor da assinatura mensal para todos os usuários.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPriceDialog(false)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpdatePrice}
              disabled={loading || !newPrice}
              className="bg-gradient-to-r from-green-600 to-emerald-600"
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Exclusão - Etapa 1 */}
      <Dialog open={deleteDialogStep === 1} onOpenChange={(open) => !open && cancelDelete()}>
        <DialogContent className="glass border-red-500/30 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-yellow-500" />
              Confirmar Exclusão (1/3)
            </DialogTitle>
            <DialogDescription className="text-gray-300">
              Você está prestes a excluir o usuário:
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-white font-semibold">{userToDelete?.name}</p>
              <p className="text-gray-400 text-sm">{userToDelete?.email}</p>
            </div>
            <p className="text-yellow-400 text-sm mt-4">
              ⚠️ Esta ação irá excluir TODOS os dados deste usuário, incluindo empresas, lançamentos e configurações.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={cancelDelete} className="border-white/20 text-white">
              Cancelar
            </Button>
            <Button onClick={proceedToStep2} className="bg-yellow-600 hover:bg-yellow-700">
              Continuar →
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Exclusão - Etapa 2 */}
      <Dialog open={deleteDialogStep === 2} onOpenChange={(open) => !open && cancelDelete()}>
        <DialogContent className="glass border-red-500/30 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-orange-500" />
              Confirmar Exclusão (2/3)
            </DialogTitle>
            <DialogDescription className="text-gray-300">
              Você tem certeza ABSOLUTA que deseja excluir este usuário?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg space-y-2">
              <p className="text-orange-300 font-medium">❌ Dados que serão perdidos:</p>
              <ul className="text-gray-400 text-sm list-disc list-inside space-y-1">
                <li>Todas as empresas vinculadas</li>
                <li>Todos os lançamentos financeiros</li>
                <li>Todos os orçamentos e propostas</li>
                <li>Todas as configurações</li>
                <li>Histórico de assinatura</li>
              </ul>
            </div>
            <p className="text-red-400 text-sm mt-4 font-semibold">
              🚫 Esta ação é IRREVERSÍVEL!
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={cancelDelete} className="border-white/20 text-white">
              Cancelar
            </Button>
            <Button onClick={proceedToStep3} className="bg-orange-600 hover:bg-orange-700">
              Sim, quero excluir →
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Exclusão - Etapa 3 (Final) */}
      <Dialog open={deleteDialogStep === 3} onOpenChange={(open) => !open && cancelDelete()}>
        <DialogContent className="glass border-red-500/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="w-6 h-6 text-red-500" />
              Última Confirmação (3/3)
            </DialogTitle>
            <DialogDescription className="text-gray-300">
              Para confirmar a exclusão, digite <strong className="text-red-400">EXCLUIR</strong> no campo abaixo:
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
              <p className="text-white font-semibold">{userToDelete?.name}</p>
              <p className="text-gray-400 text-sm">{userToDelete?.email}</p>
            </div>
            <div>
              <Label className="text-gray-300">Digite EXCLUIR para confirmar:</Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                placeholder="EXCLUIR"
                className="bg-white/5 border-red-500/30 text-white mt-2 font-mono"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={cancelDelete} className="border-white/20 text-white">
              Cancelar
            </Button>
            <Button 
              onClick={executeDeleteUser} 
              disabled={deleteConfirmText !== 'EXCLUIR' || loading}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              {loading ? 'Excluindo...' : '🗑️ Excluir Permanentemente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
